import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as authService from '@/services/authService';
import type { CustomerUser } from '@/services/authService';
import type { RootState } from '@/store';
import { simplifyErrorMessage } from '@/utils/friendlyError';

const ONBOARDING_KEY = '@salunbd/has-seen-onboarding';

interface AuthState {
  isBootstrapping: boolean;
  hasSeenOnboarding: boolean;
  isAuthenticated: boolean;
  user: CustomerUser | null;
  token: string | null;
  refreshToken: string | null;
  isSubmitting: boolean;
  error: string | null;
  /** Set once registerUser succeeds — the email a verification code was just sent to, so the
   *  verification screen knows who it's confirming without the caller re-passing it around. */
  pendingVerificationEmail: string | null;
}

const initialState: AuthState = {
  isBootstrapping: true,
  hasSeenOnboarding: false,
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  isSubmitting: false,
  error: null,
  pendingVerificationEmail: null,
};

function errorMessage(err: unknown): string {
  // One plain sentence: a network failure or server internals become a simple message.
  return simplifyErrorMessage(err instanceof Error ? err.message : null, 'Something went wrong. Please try again.');
}

/** Reads the persisted onboarding flag and, separately, whatever real session authService last
 *  persisted — this is what the splash screen waits on before deciding where to route the user. */
export const bootstrapAuth = createAsyncThunk('auth/bootstrap', async () => {
  const [seenOnboarding, session] = await Promise.all([AsyncStorage.getItem(ONBOARDING_KEY), authService.loadSession()]);
  // Someone with a saved session is past onboarding, whichever way they signed in.
  return { hasSeenOnboarding: seenOnboarding === 'true' || !!session, session };
});

/** Signing in or verifying an account means onboarding is behind them. "I already have an account"
 *  skips the onboarding screens, so without this the flag was never saved and every relaunch sent a
 *  signed-in customer back to the welcome screen. */
const rememberOnboarded = () => AsyncStorage.setItem(ONBOARDING_KEY, 'true').catch(() => undefined);

export const completeOnboarding = createAsyncThunk('auth/completeOnboarding', async () => {
  await AsyncStorage.setItem(ONBOARDING_KEY, 'true');
});

export const registerUser = createAsyncThunk(
  'auth/register',
  async (input: { name: string; email: string; phone?: string; password: string }, { rejectWithValue }) => {
    try {
      return await authService.register(input);
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
);

export const verifyEmailCode = createAsyncThunk('auth/verifyEmail', async (input: { email: string; code: string }, { rejectWithValue }) => {
  try {
    const session = await authService.verifyEmail(input.email, input.code);
    await rememberOnboarded();
    return session;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const resendCode = createAsyncThunk('auth/resendCode', async (email: string, { rejectWithValue }) => {
  try {
    await authService.resendVerification(email);
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const loginUser = createAsyncThunk('auth/login', async (input: { email: string; password: string }, { rejectWithValue }) => {
  try {
    const session = await authService.login(input);
    await rememberOnboarded();
    return session;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

/** Resolves to null when the person closed Google's account picker. */
export const googleLogin = createAsyncThunk('auth/googleLogin', async (idToken: string, { rejectWithValue }) => {
  try {
    const session = await authService.googleSignIn(idToken);
    await rememberOnboarded();
    return session;
  } catch (err) {
    return rejectWithValue(errorMessage(err));
  }
});

export const signOut = createAsyncThunk('auth/signOut', async () => {
  await authService.logout();
});

// Real "sign out everywhere" — revokes every refresh token server-side, not just this device's.
export const signOutAllDevices = createAsyncThunk('auth/signOutAllDevices', async () => {
  await authService.logoutAll();
});

export const changeUserPassword = createAsyncThunk(
  'auth/changePassword',
  async (input: { currentPassword: string; newPassword: string }, { getState, rejectWithValue }) => {
    const token = (getState() as RootState).auth.token;
    if (!token) return rejectWithValue('Your session has expired. Please sign in again.');
    try {
      return await authService.changePassword(token, input.currentPassword, input.newPassword);
    } catch (err) {
      return rejectWithValue(errorMessage(err));
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError(state) {
      state.error = null;
    },
    // Dispatched by the RTK Query base query after a transparent 401 refresh, so the next call
    // already carries the fresh token instead of racing another needless refresh.
    tokensRefreshed(state, action: PayloadAction<{ token: string; refreshToken: string }>) {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      // A successful refresh IS proof of a valid session, but this used to leave `isAuthenticated`
      // untouched. If it was ever false at the moment a refresh landed (e.g. a burst of requests
      // racing bootstrap), the app stayed permanently "logged-out-looking" from then on — every
      // screen gated on `user`/`isAuthenticated` (messages, booking history, loyalty,
      // notifications) silently showed nothing forever, even holding a perfectly valid, freshly
      // refreshed token. Only flip it when `user` is already populated: if it isn't, something
      // deeper is wrong than a stale flag, and claiming authentication without a user record would
      // just break every screen that reads `user.name`/`user.email`.
      if (state.user) {
        state.isAuthenticated = true;
      }
    },
    updateUserProfileData(state, action: PayloadAction<{ name?: string; phone?: string; avatar?: string }>) {
      if (state.user) {
        if (action.payload.name !== undefined) state.user.name = action.payload.name;
        if (action.payload.phone !== undefined) state.user.phone = action.payload.phone;
        if (action.payload.avatar !== undefined) state.user.avatar = action.payload.avatar;
      }
    },
    setSession(state, action: PayloadAction<authService.Session>) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapAuth.fulfilled, (state, action) => {
        state.hasSeenOnboarding = action.payload.hasSeenOnboarding;
        state.isAuthenticated = !!action.payload.session;
        state.user = action.payload.session?.user ?? null;
        state.token = action.payload.session?.token ?? null;
        state.refreshToken = action.payload.session?.refreshToken ?? null;
        state.isBootstrapping = false;
      })
      .addCase(bootstrapAuth.rejected, (state) => {
        state.isBootstrapping = false;
        state.hasSeenOnboarding = false;
      })
      .addCase(completeOnboarding.fulfilled, (state) => {
        state.hasSeenOnboarding = true;
      })
      .addCase(registerUser.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.pendingVerificationEmail = action.payload.email;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Registration failed.';
      })
      .addCase(verifyEmailCode.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(verifyEmailCode.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isAuthenticated = true;
        state.hasSeenOnboarding = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.pendingVerificationEmail = null;
      })
      .addCase(verifyEmailCode.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Verification failed.';
      })
      .addCase(resendCode.rejected, (state, action) => {
        state.error = (action.payload as string) ?? action.error.message ?? 'Could not resend the code.';
      })
      .addCase(loginUser.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isAuthenticated = true;
        state.hasSeenOnboarding = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Login failed.';
      })
      .addCase(googleLogin.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isAuthenticated = true;
        state.hasSeenOnboarding = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = (action.payload as string) ?? action.error.message ?? 'Google sign-in failed.';
      })
      .addCase(signOut.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      .addCase(signOutAllDevices.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      .addCase(changeUserPassword.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      });
  },
});

export const { clearAuthError, tokensRefreshed, updateUserProfileData, setSession } = authSlice.actions;
export default authSlice.reducer;
